import React from 'react'
import { cn } from 'lib/utils'

interface DateSeparatorProps {
	/** The date to show. Ignored when `children` supplies its own pill. */
	label?: string
	/**
	 * A custom pill, for callers that need it to do something — the room chat
	 * passes a dropdown trigger so the pill doubles as a jump-to control.
	 */
	children?: React.ReactNode
	/**
	 * Pin the row to the top of the scrolling ancestor, so the current day's
	 * pill stays visible while its items scroll past. Requires an ancestor with
	 * its own overflow; on a page that scrolls as a whole it does nothing.
	 */
	sticky?: boolean
	className?: string
}

/**
 * A hairline rule with a centred pill on it, marking a day boundary in a list.
 *
 * Extracted from the room chat's message divider so the admin image library can
 * show the same thing. The white background is load-bearing rather than
 * decoration: when the row is sticky, content scrolling underneath would
 * otherwise show through the pill and the rules either side of it.
 */
const DateSeparator: React.FC<DateSeparatorProps> = ({
	label,
	children,
	sticky = false,
	className,
}) => (
	<div
		className={cn(
			'flex items-center bg-white py-2',
			sticky && 'sticky top-0 z-10',
			className,
		)}
	>
		<span className="h-px flex-1 bg-gray-200" />

		{children ?? (
			<span className="mx-3 inline-flex items-center rounded-full border border-gray-200 bg-white px-3 py-1 text-[13px] font-semibold text-gray-700 shadow-sm">
				{label}
			</span>
		)}

		<span className="h-px flex-1 bg-gray-200" />
	</div>
)

export default DateSeparator
